import React, { useState, useRef, useEffect } from 'react';
import { Image as ImageIcon, X, Zap } from 'lucide-react';
import jsQR from 'jsqr';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { getQRResult } from '@/network/api.service';

const COOLDOWN_PERIOD = 5000;
const EMPTY_BED_COOLDOWN = 60 * 1000;

const QRScannerPage = () => {
  const [scanning, setScanning] = useState(true);
  const [flashOn, setFlashOn] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [cameraPermission, setCameraPermission] = useState('checking');

  const emptyBedCooldownRef = useRef(new Map());
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const cameraRequestInProgressRef = useRef(false);
  const toastShownRef = useRef(false);

  const apiCallInProgressRef = useRef(false);
  const failedQRCodesRef = useRef(new Map()); // Track failed QR codes with timestamps
  const lastProcessedQRRef = useRef(null);

  const navigate = useNavigate();

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);

  useEffect(() => {
    if (!navigator.permissions) {
      setCameraPermission('denied');
      return;
    }

    navigator.permissions
      .query({ name: 'camera' })
      .then(result => {
        if (result.state === 'granted') {
          setCameraPermission('granted');
          startCamera(); // Start camera if already granted
        } else if (result.state === 'prompt') {
          setCameraPermission('denied');
          // Try to start camera anyway (will prompt user)
          startCamera();
        } else {
          setCameraPermission('blocked');
        }

        result.onchange = () => {
          if (result.state === 'granted') {
            setCameraPermission('granted');
            startCamera();
          } else {
            setCameraPermission('blocked');
            stopCamera();
          }
        };
      })
      .catch(() => {
        // If permissions API fails, try to start camera directly
        setCameraPermission('denied');
        startCamera();
      });

    return () => stopCamera();
  }, []);

  useEffect(() => {
    if (scanning && videoRef.current && canvasRef.current) {
      startScanning();
    } else {
      stopScanning();
    }
    return () => {
      stopScanning();
    };
  }, [scanning]);

  /* -------------------- Camera -------------------- */
  const startCamera = async () => {
    // Prevent multiple simultaneous calls
    if (cameraRequestInProgressRef.current) {
      return;
    }

    // If camera is already running, don't start again
    if (streamRef.current && streamRef.current.active) {
      return;
    }

    cameraRequestInProgressRef.current = true;
    toastShownRef.current = false;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            if (error.name !== 'AbortError') {
              console.error('Error playing video:', error);
            }
          });
        }
      }

      setCameraPermission('granted');
      setScanning(true);
      toastShownRef.current = false;
    } catch (err) {
      if (!toastShownRef.current) {
        toastShownRef.current = true;
        setCameraPermission('denied');
        toast.error('Camera permission required');
      }
    } finally {
      cameraRequestInProgressRef.current = false;
    }
  };

  const stopCamera = () => {
    stopScanning();
    cameraRequestInProgressRef.current = false;
    toastShownRef.current = false;

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  /* -------------------- QR scanning -------------------- */
  const startScanning = () => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
    }

    scanIntervalRef.current = setInterval(() => {
      if (videoRef.current && canvasRef.current && videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA) {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        canvas.height = video.videoHeight;
        canvas.width = video.videoWidth;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);

        if (code) {
          setScanning(false);
          stopScanning();
          handleQRResult(code.data);
        }
        setScanning(true);
        startScanning();
      }
    }, 300);
  };

  const stopScanning = () => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
  };

  const cleanQRPath = (pathname, search) => {
    let path = `${pathname}${search}`;

    // Remove /api-server prefix if it exists (since apiAxios baseURL already includes it)
    if (path.startsWith('/api-server')) {
      path = path.replace('/api-server', '');
    }

    // Ensure path starts with /
    if (!path.startsWith('/')) {
      path = '/' + path;
    }

    return path;
  };

  /* -------------------- QR result -------------------- */
  const handleQRResult = async qrUrl => {
    if (apiCallInProgressRef.current) {
      return;
    }
    try {
      let cleanedPath;
      const parsedUrl = new URL(qrUrl);

      if (parsedUrl.searchParams.get('redirect') === 'true') {
        parsedUrl.searchParams.set('redirect', 'false');
      }

      // cleanedPath = `${parsedUrl.pathname}${parsedUrl.search}`;
      cleanedPath = cleanQRPath(parsedUrl.pathname, parsedUrl.search);

      const emptyBedTimestamp = emptyBedCooldownRef.current.get(cleanedPath);

      if (emptyBedTimestamp) {
        const timeSinceEmpty = Date.now() - emptyBedTimestamp;

        if (timeSinceEmpty < EMPTY_BED_COOLDOWN) {
          return;
        } else {
          emptyBedCooldownRef.current.delete(cleanedPath); // remove from cooldown
        }
      }

      // Check if this QR cod recently failed
      const failedTimestamp = failedQRCodesRef.current.get(cleanedPath);

      if (failedTimestamp) {
        const timeSinceFailure = Date.now() - failedTimestamp;
        if (timeSinceFailure < COOLDOWN_PERIOD) {
          // Skip API call if still in cooldown period
          return;
        } else {
          // Remove from failed list if cooldown has passed
          failedQRCodesRef.current.delete(cleanedPath);
        }
      }

      // Check if this is the same QR code we just processed
      if (lastProcessedQRRef.current === cleanedPath && !failedTimestamp) {
        return;
      }

      apiCallInProgressRef.current = true;
      lastProcessedQRRef.current = cleanedPath;

      const response = await getQRResult(cleanedPath);
      const data = response.data;

      // Success - remove from failed list if it was there
      failedQRCodesRef.current.delete(cleanedPath);

      if (data?.data?.bed_info?.is_empty) {
        emptyBedCooldownRef.current.set(cleanedPath, Date.now());
        toast.error(`Bed ${data?.data?.bed_info?.bed_number} is empty`);

        return;
      }

      if (data?.data?.redirect_url) {
        setScanning(false);
        stopScanning();
        stopCamera();
        // navigate(data?.data?.redirect_url);
        setTimeout(() => {
          navigate(data?.data?.redirect_url);
        }, 100);
      }
    } catch (error) {
      const parsedUrl = new URL(qrUrl);
      if (parsedUrl.searchParams.get('redirect') === 'true') {
        parsedUrl.searchParams.set('redirect', 'false');
      }
      const cleanedPath = `${parsedUrl.pathname}${parsedUrl.search}`;

      // Track failed QR code with current timestamp
      failedQRCodesRef.current.set(cleanedPath, Date.now());

      lastProcessedQRRef.current = null;

      toast.error(error.response?.data?.message || 'Failed to process QR code. Please try again.');
      setScanning(true);
    } finally {
      apiCallInProgressRef.current = false;
    }
  };

  /* -------------------- Flash -------------------- */
  const toggleFlash = async () => {
    try {
      const track = streamRef.current?.getVideoTracks?.()[0];
      if (!track) return;

      const capabilities = track.getCapabilities?.();
      if (!capabilities?.torch) {
        toast.error('Flash not supported on this device');
        return;
      }

      await track.applyConstraints({ advanced: [{ torch: !flashOn }] });
      setFlashOn(v => !v);
    } catch {
      toast.error('Unable to toggle flash');
    }
  };

  /* -------------------- Image upload -------------------- */
  const handleImageUpload = e => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = event => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = canvasRef.current;
          const context = canvas.getContext('2d');

          canvas.width = img.width;
          canvas.height = img.height;
          context.drawImage(img, 0, 0);

          const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(imageData.data, imageData.width, imageData.height);

          if (code) {
            handleQRResult(code.data);
          } else {
            toast.error('No QR code found in the image');
          }
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  /* -------------------- Render -------------------- */
  return (
    <div className="relative h-full w-full overflow-hidden bg-black">
      {/* Camera permission */}
      {cameraPermission !== 'granted' && (
        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-black/85 px-6 text-white">
          {cameraPermission === 'checking' && <p className="text-lg font-medium">Checking camera permission…</p>}

          {cameraPermission === 'denied' && (
            <>
              <p className="mb-4 text-center text-lg font-medium">Camera access is required to scan QR codes</p>
              <button onClick={startCamera} className="rounded-lg bg-blue-600 px-6 py-2 text-sm font-semibold">
                Allow Camera
              </button>
              <p className="mt-4 text-xs opacity-70">Or upload a QR image instead</p>
            </>
          )}

          {cameraPermission === 'blocked' && (
            <>
              <p className="text-center text-lg font-medium">Camera access is blocked</p>
              <p className="mt-2 max-w-xs text-center text-sm opacity-80">
                Enable camera permission from browser settings
              </p>
              {isIOS && (
                <div className="mt-4 rounded bg-gray-900 px-4 py-2 text-xs text-gray-200">
                  Settings → Safari → Camera → Allow
                </div>
              )}

              {/* Android */}
              {isAndroid && (
                <div className="mt-4 rounded bg-gray-900 px-4 py-2 text-xs text-gray-200">
                  Settings → Apps → Browser → Permissions → Camera → Allow
                </div>
              )}

              {/* Desktop */}
              {!isIOS && !isAndroid && (
                <div className="mt-4 rounded bg-gray-900 px-4 py-2 text-xs text-gray-200">
                  Browser address bar → Lock icon → Site settings → Camera → Allow
                </div>
              )}
            </>
          )}
        </div>
      )}
      {/* Camera View */}
      <div className="absolute inset-0">
        <video ref={videoRef} className="h-full w-full object-cover" playsInline autoPlay />
        <canvas ref={canvasRef} className="hidden" />
      </div>

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-40"></div>

      {/* Top Bar */}
      <div className="absolute left-0 right-0 top-0 z-20 flex items-center justify-between p-4">
        <button
          onClick={() => {
            window.history.back();
            stopScanning();
            stopCamera();
          }}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-black bg-opacity-50 text-white transition-all hover:bg-opacity-70"
        >
          <X size={24} />
        </button>

        <div className="flex gap-2">
          <button
            onClick={toggleFlash}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-black bg-opacity-50 text-white transition-all hover:bg-opacity-70"
          >
            {flashOn ? <Zap size={20} fill="white" /> : <Zap size={20} />}
          </button>
        </div>
      </div>

      {/* Scanner Frame */}
      <div className="absolute inset-0 z-10 flex items-center justify-center">
        <div className="relative">
          {/* Scanning area */}
          <div className="relative h-72 w-72">
            {/* Corner brackets */}
            <div className="absolute -left-1 -top-1 h-12 w-12">
              <div className="absolute left-0 top-0 h-1 w-full rounded-full bg-neutral-200"></div>
              <div className="absolute left-0 top-0 h-full w-1 rounded-full bg-neutral-200"></div>
            </div>
            <div className="absolute -right-1 -top-1 h-12 w-12">
              <div className="absolute right-0 top-0 h-1 w-full rounded-full bg-neutral-200"></div>
              <div className="absolute right-0 top-0 h-full w-1 rounded-full bg-neutral-200"></div>
            </div>
            <div className="absolute -bottom-1 -left-1 h-12 w-12">
              <div className="absolute bottom-0 left-0 h-1 w-full rounded-full bg-neutral-200"></div>
              <div className="absolute bottom-0 left-0 h-full w-1 rounded-full bg-neutral-200"></div>
            </div>
            <div className="absolute -bottom-1 -right-1 h-12 w-12">
              <div className="absolute bottom-0 right-0 h-1 w-full rounded-full bg-neutral-200"></div>
              <div className="absolute bottom-0 right-0 h-full w-1 rounded-full bg-neutral-200"></div>
            </div>

            {/* Scanning line animation */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute h-1 w-full animate-scan bg-gradient-to-r from-transparent via-blue-400 to-transparent"></div>
            </div>
          </div>

          {/* Instruction text */}
          <div className="absolute -bottom-16 left-1/2 -translate-x-1/2 transform whitespace-nowrap">
            <p className="text-center text-lg font-medium text-white drop-shadow-lg">Scan QR code</p>
          </div>
        </div>
      </div>

      {/* Bottom Sheet */}
      <div
        className={`absolute bottom-0 left-0 right-0 z-20 rounded-t-3xl bg-white shadow-2xl transition-transform duration-300 ease-out ${
          drawerOpen ? 'translate-y-0' : 'min-h-[30px] translate-y-[calc(100%-30px)]'
        }`}
      >
        {/* Handle - Clickable to toggle */}
        <button onClick={() => setDrawerOpen(!drawerOpen)} className="w-full cursor-pointer pb-2 pt-3">
          <div className="flex justify-center">
            <div className="h-1 w-10 rounded-full bg-gray-300"></div>
          </div>
        </button>

        {/* Content */}
        <div className={`overflow-hidden transition-all duration-300 ${drawerOpen ? 'max-h-[85vh]' : 'max-h-0'}`}>
          <div className="px-6 pb-8 pt-2">
            {/* Upload Image Option */}
            <label className="flex cursor-pointer items-center gap-4 rounded-2xl p-4 transition-colors hover:bg-gray-50">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50">
                <ImageIcon size={24} className="text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Upload from gallery</h3>
                <p className="text-sm text-gray-500">Select a QR code image</p>
              </div>
              <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
            </label>

            {/* Divider */}
            <div className="my-4 border-t border-gray-100"></div>

            {/* Info Section */}
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                  <Zap size={12} className="text-blue-600" />
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Instant detection</h4>
                  <p className="mt-0.5 text-xs text-gray-500">Automatic scanning when QR code is in frame</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRScannerPage;
